// <house-md> — the form-associated custom element that ties everything
// together. Owns the Document, the input handlers, and the IME
// composition lifecycle.
//
// IME / dead-key composition (e.g. macOS Option+E + e → é) is handled
// here, not in InputHandler. The strategy:
//
//   compositionstart  — set composing=true, snapshot the pre-composition
//                       Selection range. From this point on, beforeinput
//                       does NOT preventDefault, so the browser drives
//                       the contenteditable display natively.
//   compositionend    — reset the History throttle, replace the snapshot
//                       range with event.data, re-render. The content
//                       model converges with the live DOM.
//
// The submission value travels through a plain hidden <input> that the
// element renders and keeps in sync (see installShell / writeFormValue),
// NOT through ElementInternals.setFormValue. setFormValue is the obvious
// choice for a form-associated element and it is a trap here: the only
// hooks to refresh it are the `value` setter (which the typing path
// bypasses) and a `formdata` listener, and the HTML spec builds the entry
// list BEFORE `formdata` fires — so a listener can only ever affect the
// NEXT submission. Every save shipped the previous save's text.
//
// A real control has neither problem. It is read from the live DOM at
// submit time by native submit, requestSubmit(), and `new FormData(form)`
// alike, and it is associated with whatever <form> currently contains it —
// so a Turbo morph that swaps the <form> node out from under a
// data-turbo-permanent editor cannot strand it either.
//
// The element stays form-associated for `internals.form`, constraint
// validation (required / setValidity), and the textbox ARIA role. It just
// never calls setFormValue, so it contributes no entry of its own and the
// hidden input is the single source of the submitted value.

import { Document } from "./document.js"
import { Selection } from "./selection.js"
import { InputHandler } from "./input_handler.js"
import { ActionHandler } from "./action_handler.js"
import { DragAndDropHandler } from "./drag_and_drop.js"

const KEYBOARD_SHORTCUTS = {
  "Ctrl+B": "bold",
  "Ctrl+I": "italic",
  "Ctrl+S": "strikethrough",
  "Ctrl+Z": "undo",
  "Ctrl+Shift+Z": "redo",
}

export class Editor extends HTMLElement {
  static formAssociated = true
  static observedAttributes = ["name"]

  constructor() {
    super()
    this.internals = this.attachInternals()
    this.internals.ariaRole = "textbox"
    this.composing = false
    this.compositionStartRange = null
  }

  connectedCallback() {
    const initialContent = this.querySelector(".house-md-content")?.textContent || this.textContent
    this.document = new Document(initialContent, this)
    this.installShell()
    this.attachEventListeners()
    this.inputHandler = new InputHandler(this.document)
    this.actionHandler = new ActionHandler(this.document)
    this.dropAndDropHandler = new DragAndDropHandler(this)
    if (this.autofocus) this.focus()
    this.setAttribute("initialized", "")
    this.value = initialContent
  }

  disconnectedCallback() {
    this.detachEventListeners()
  }

  validate = (event) => {
    if (this.required && this.document.isEmpty) {
      event.preventDefault()
      this.internals.setValidity({ valueMissing: true }, "This field is required.", this.contentWrapper)
      this.focus()
    } else {
      this.internals.setValidity({})
    }
  }

  render() {
    this.document.render()
  }

  focus() {
    this.contentWrapper.focus()
  }

  get required() {
    return this.hasAttribute("required")
  }

  set required(value) {
    if (value) this.setAttribute("required", "")
    else this.removeAttribute("required")
  }

  get autofocus() {
    return this.hasAttribute("autofocus")
  }

  set autofocus(value) {
    if (value) this.setAttribute("autofocus", "")
    else this.removeAttribute("autofocus")
  }

  get value() {
    return this.document.content
  }

  // Assigning `value` is an external, programmatic write, so — like
  // `input.value = x` on a native control — it does NOT dispatch `input`.
  // The typing path bypasses this setter entirely and syncs through
  // Document#commit instead.
  set value(text) {
    this.document.content = text
    this.writeFormValue(text)
    this.internals.setValidity({})
    this.render()
  }

  get form() {
    return this.internals.form
  }

  installShell() {
    this.textContent = ""
    this.toolbar = this.findOrCreateToolbar()
    this.contentWrapper = document.createElement("div")
    this.contentWrapper.classList.add("house-md-content")
    this.contentWrapper.setAttribute("contenteditable", "true")
    this.append(this.contentWrapper)
    if (!this.getAttribute("tabindex")) this.contentWrapper.setAttribute("tabindex", "0")
    this.installHiddenInput()
  }

  // The hidden input carries the value into the form. It has to be a sibling
  // of contentWrapper rather than a child: Document#render calls
  // replaceChildren() on the content element, so anything inside is wiped on
  // the next keystroke.
  installHiddenInput() {
    this.hiddenInput = document.createElement("input")
    this.hiddenInput.type = "hidden"
    this.hiddenInput.name = this.getAttribute("name") || ""
    this.append(this.hiddenInput)
  }

  // Called from the `value` setter and from Document#commit — i.e. on every
  // path that changes the content, so the input is never stale.
  writeFormValue(text) {
    if (this.hiddenInput) this.hiddenInput.value = text
  }

  attributeChangedCallback(name, previous, current) {
    if (name === "name" && this.hiddenInput) this.hiddenInput.name = current || ""
  }

  findOrCreateToolbar() {
    const externalId = this.getAttribute("toolbar")
    if (externalId) return document.getElementById(externalId)
    const toolbar = document.createElement("house-md-toolbar")
    this.prepend(toolbar)
    return toolbar
  }

  attachEventListeners() {
    this.addEventListener("beforeinput", this.onBeforeInput)
    this.addEventListener("compositionstart", this.onCompositionStart)
    this.addEventListener("compositionend", this.onCompositionEnd)
    this.addEventListener("keydown", this.onKeyDown)
    this.addEventListener("dragover", this.onDragOver)
    this.addEventListener("drop", this.onDrop)
    this.toolbar.addEventListener("house-md:toolbar-action", this.onToolbarAction)
    document.addEventListener("selectionchange", this.onSelectionChange)
    if (this.internals.form) {
      this.internals.form.addEventListener("submit", this.validate)
    }
  }

  detachEventListeners() {
    this.removeEventListener("beforeinput", this.onBeforeInput)
    this.removeEventListener("compositionstart", this.onCompositionStart)
    this.removeEventListener("compositionend", this.onCompositionEnd)
    this.removeEventListener("keydown", this.onKeyDown)
    this.removeEventListener("dragover", this.onDragOver)
    this.removeEventListener("drop", this.onDrop)
    this.toolbar.removeEventListener("house-md:toolbar-action", this.onToolbarAction)
    document.removeEventListener("selectionchange", this.onSelectionChange)
    if (this.internals.form) {
      this.internals.form.removeEventListener("submit", this.validate)
    }
  }

  onBeforeInput = (event) => {
    this.inputHandler.handleInput(event, this.contentWrapper, this.document.blocks)
  }

  onCompositionStart = () => {
    this.composing = true
    const live = window.getSelection()
    if (live && live.rangeCount > 0) {
      this.compositionStartRange = Selection.fromDOMRange(
        live.getRangeAt(0),
        this.contentWrapper,
        this.document.blocks,
      )
    } else {
      const { start, end } = this.document.currentSelection
      this.compositionStartRange = { start, end }
    }
  }

  onCompositionEnd = (event) => {
    const snapshot = this.compositionStartRange
    this.composing = false
    this.compositionStartRange = null
    if (!snapshot) return
    // Reset the History throttle so the upcoming replaceText records a
    // new undo entry covering the entire composition rather than being
    // dropped if a recent typing burst left the throttle armed.
    this.document.history.resetThrottle()
    this.document.replaceText(event.data || "", snapshot.start, snapshot.end)
  }

  onKeyDown = (event) => {
    const action = KEYBOARD_SHORTCUTS[shortcutKey(event)]
    if (!action) return
    event.preventDefault()
    // Stop the consumed shortcut from bubbling to global/@window listeners
    // (e.g. an app sidebar bound to Ctrl+B). Keys the editor does NOT handle
    // fall through above and keep bubbling, so non-editor shortcuts still work.
    event.stopPropagation()
    this.actionHandler.handleAction(action)
  }

  onDragOver = (event) => {
    this.dropAndDropHandler.handleDragOver(event)
  }

  onDrop = (event) => {
    this.dropAndDropHandler.handleDrop(event)
  }

  onToolbarAction = ({ detail }) => {
    this.actionHandler.handleAction(detail.houseMdAction, detail)
  }

  onSelectionChange = () => {
    if (this.composing) return
    this.document.selection.update()
  }
}

function shortcutKey(event) {
  const parts = []
  if (event.metaKey || event.ctrlKey) parts.push("Ctrl")
  if (event.altKey) parts.push("Alt")
  if (event.shiftKey) parts.push("Shift")
  parts.push(event.key.toUpperCase())
  return parts.join("+")
}

customElements.define("house-md", Editor)
